import { sql } from "drizzle-orm"
import {
  boolean,
  foreignKey,
  index,
  integer,
  pgEnum,
  pgPolicy,
  pgTable,
  timestamp,
  unique,
  varchar,
} from "drizzle-orm/pg-core"
import { authenticatedRole } from "drizzle-orm/supabase"

function userId<T>(c: T) {
  return sql`((SELECT auth.uid() AS user_id) = ${c}::uuid)`
}

function publicPolicy(name: string) {
  return pgPolicy(name, {
    for: "all",
    as: "permissive",
    to: authenticatedRole,
    using: sql<boolean>`true`,
  })
}

export const accrualMethodEnum = [
  "ImportedRaindrop",
  "ImportedPocket",
  "ImportedHtml",
  "Direct",
] as const
export type AccrualMethods = (typeof accrualMethodEnum)[number]
export const accrualMethodPgEnum = pgEnum("accrualmethod", accrualMethodEnum)

export const emailDigestEnum = ["daily", "weekly"] as const
export type EmailDigests = (typeof emailDigestEnum)[number]
export const emailDigestPgEnum = pgEnum("emaildigest", emailDigestEnum)

export const logStatusEnum = ["Success", "Failed", "Progress", "Poke"] as const
export type LogStatuses = (typeof logStatusEnum)[number]
export const logStatusPgEnum = pgEnum("status", logStatusEnum)

const base = {
  id: varchar().primaryKey().notNull(),
  created_at: timestamp("created_at", {
    withTimezone: true,
  })
    .defaultNow()
    .notNull(),
  updated_at: timestamp("updated_at", { withTimezone: true })
    .defaultNow()
    .notNull()
    .$onUpdate(() => new Date()),
}

export const users = pgTable(
  "users",
  {
    ...base,
    email: varchar().notNull(),
    name: varchar(),
    title: varchar(),
    description: varchar(),
    blog_footer: varchar(),
    blog_header: varchar(),
    logo_url: varchar("logo_url"),
    wordmark_url: varchar("wordmark_url"),
    welcome_image_url: varchar("welcome_image_url"),
    last_sign_in_at: timestamp("last_sign_in_at", {
      withTimezone: true,
    }).notNull(),
    email_digest: emailDigestPgEnum().default("daily"),
    share_bookmarks: boolean("share_bookmarks").default(true).notNull(),
  },
  t => [
    pgPolicy("users_private", {
      for: "all",
      to: authenticatedRole,
      using: userId(t.id),
      withCheck: userId(t.id),
    }),
    unique("ix__users__email").on(t.email),
    unique("ix__users__name").on(t.name),
  ],
)

export const subscriptions = pgTable(
  "subscriptions",
  {
    ...base,
    user_id: varchar("user_id").notNull(),
    publisher_id: varchar("publisher_id").notNull(),
    silenced: boolean().default(false).notNull(),
  },
  t => [
    pgPolicy("subscriptions_private", {
      for: "all",
      to: authenticatedRole,
      withCheck: userId(t.user_id),
      using: userId(t.user_id),
    }),
    index("ix__subscriptions__publisher_id").on(t.publisher_id),
    index("ix__subscriptions__user_id").on(t.user_id),
    unique("ix__subscriptions__user_id_publisher_id").on(
      t.user_id,
      t.publisher_id,
    ),
    foreignKey({
      columns: [t.publisher_id],
      foreignColumns: [publishers.id],
      name: "fk__subscriptions__publisher_id__publishers",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__subscriptions__user_id__users",
    }).onDelete("cascade"),
  ],
)

export const stories = pgTable(
  "stories",
  {
    ...base,
    url: varchar().notNull(),
    metadated_at: timestamp("metadated_at", {
      withTimezone: true,
    }),
    feed_url: varchar("feed_url"),
    dc_created_at: timestamp("dc_created_at", {
      withTimezone: true,
    }),
    dc_creator: varchar("dc_creator"),
    dc_description: varchar("dc_description"),
    dc_language: varchar("dc_language"),
    dc_modified_at: timestamp("dc_modified_at", {
      withTimezone: true,
    }),
    dc_title: varchar("dc_title"),
    dc_identifier: varchar("dc_identifier"),
    truncated_body: varchar("truncated_body"),
    metadated_attempts: integer("metadated_attempts").default(0).notNull(),
    summary: varchar("summary"),
    tags: varchar("tags"),
    image_url: varchar("image_url"),
    logo_url: varchar("logo_url"),
  },
  t => [
    publicPolicy("stories_authenticated"),
    index("ix__stories__dc_created_at").on(t.dc_created_at),
    index("ix__stories__feed_url").on(t.feed_url),
    unique("ix__stories__url").on(t.url),
  ],
)

export const replicacheCvr = pgTable(
  "replicache_cvr",
  {
    ...base,
    json: varchar().notNull(),
  },
  _t => [publicPolicy("replicache_cvr_authenticated")],
)

export const alembicVersion = pgTable(
  "alembic_version",
  {
    version_num: varchar("version_num", { length: 32 }).primaryKey().notNull(),
  },
  _t => [publicPolicy("alembic_version_authenticated")],
)

export const recentPublishers = pgTable(
  "recent_publishers",
  {
    ...base,
    publisher_id: varchar("publisher_id").notNull(),
    user_id: varchar("user_id").notNull(),
  },
  t => [
    pgPolicy("recent_publishers_private", {
      for: "all",
      to: authenticatedRole,
      withCheck: userId(t.user_id),
      using: userId(t.user_id),
    }),
    foreignKey({
      columns: [t.publisher_id],
      foreignColumns: [publishers.id],
      name: "fk__recent_publishers__publisher_id__publishers",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__recent_publishers__user_id__users",
    }).onDelete("cascade"),
  ],
)

export const publishers = pgTable(
  "publishers",
  {
    ...base,
    url: varchar().notNull(),
    user_id: varchar("user_id"),
    metadated_at: timestamp("metadated_at", {
      withTimezone: true,
    }),
    dc_description: varchar("dc_description"),
    dc_title: varchar("dc_title"),
    dc_language: varchar("dc_language"),
    dc_creator: varchar("dc_creator"),
    dc_created_at: timestamp("dc_created_at", {
      withTimezone: true,
    }),
    dc_modified_at: timestamp("dc_modified_at", {
      withTimezone: true,
    }),
    dc_identifier: varchar("dc_identifier"),
    feed_etag: varchar("feed_etag"),
    feed_last_modified: varchar("feed_last_modified"),
    feed_next_polling_at: timestamp("feed_next_polling_at", {
      withTimezone: true,
    }),
    metadated_attempts: integer("metadated_attempts").default(0).notNull(),
    image_url: varchar("image_url"),
    logo_url: varchar("logo_url"),
    tags: varchar("tags"),
    polling_attempts: integer("polling_attempts").default(0).notNull(),
  },
  t => [
    publicPolicy("publishers_authenticated"),
    index("ix__publishers__feed_next_polling_at").on(t.feed_next_polling_at),
    index("ix__publishers__user_id").on(t.user_id),
    unique("ix__publishers__url").on(t.url),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__publishers__user_id__users",
    }).onDelete("set null"),
  ],
)

export const entries = pgTable(
  "entries",
  {
    ...base,
    story_id: varchar("story_id").notNull(),
    publisher_id: varchar("publisher_id").notNull(),
    published_at: timestamp("published_at", {
      withTimezone: true,
    }),
  },
  t => [
    publicPolicy("entries_authenticated"),
    index("ix__entries__publisher_id").on(t.publisher_id),
    index("ix__entries__publisher_id_published_at_story_id").on(
      t.publisher_id,
      t.published_at,
      t.story_id,
    ),
    index("ix__entries__story_id").on(t.story_id),
    unique("ix__entries__story_id_publisher_id").on(t.story_id, t.publisher_id),
    foreignKey({
      columns: [t.publisher_id],
      foreignColumns: [publishers.id],
      name: "fk__entries__publisher_id__publishers",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.story_id],
      foreignColumns: [stories.id],
      name: "fk__entries__story_id__stories",
    }).onDelete("cascade"),
  ],
)

export const bookmarks = pgTable(
  "bookmarks",
  {
    ...base,
    user_id: varchar("user_id").notNull(),
    story_id: varchar("story_id").notNull(),
    bookmarked_at: timestamp("bookmarked_at", {
      withTimezone: true,
    }).notNull(),
    dc_accrual_method: accrualMethodPgEnum("dc_accrual_method"),
  },
  t => [
    pgPolicy("bookmarks_private", {
      for: "all",
      to: authenticatedRole,
      withCheck: userId(t.user_id),
      using: userId(t.user_id),
    }),
    index("ix__bookmarks__story_id").on(t.story_id),
    index("ix__bookmarks__user_id").on(t.user_id),
    unique("ix__bookmarks__user_id_story_id").on(t.user_id, t.story_id),
    foreignKey({
      columns: [t.story_id],
      foreignColumns: [stories.id],
      name: "fk__bookmarks__story_id__stories",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__bookmarks__user_id__users",
    }).onDelete("cascade"),
  ],
)

export const bookmarkTags = pgTable(
  "bookmark_tags",
  {
    ...base,
    user_id: varchar("user_id").notNull(),
    bookmark_id: varchar("bookmark_id").notNull(),
    tag: varchar().notNull(),
  },
  t => [
    pgPolicy("bookmark_tags_private", {
      for: "all",
      to: authenticatedRole,
      withCheck: userId(t.user_id),
      using: userId(t.user_id),
    }),
    index("ix__bookmark_tags__bookmark_id").on(t.bookmark_id),
    index("ix__bookmark_tags__user_id").on(t.user_id),
    unique("ix__bookmark_tags__bookmark_id_tag").on(t.bookmark_id, t.tag),
    foreignKey({
      columns: [t.bookmark_id],
      foreignColumns: [bookmarks.id],
      name: "fk__bookmark_tags__bookmark_id__bookmarks",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__bookmark_tags__user_id__users",
    }).onDelete("cascade"),
  ],
)

export const postTags = pgTable(
  "post_tags",
  {
    ...base,
    user_id: varchar("user_id").notNull(),
    post_id: varchar("post_id").notNull(),
    tag: varchar().notNull(),
  },
  t => [
    pgPolicy("post_tags_private", {
      for: "all",
      to: authenticatedRole,
      withCheck: userId(t.user_id),
      using: userId(t.user_id),
    }),
    index("ix__post_tags__post_id").on(t.post_id),
    index("ix__post_tags__user_id").on(t.user_id),
    unique("ix__post_tags__post_id_tag").on(t.post_id, t.tag),
    foreignKey({
      columns: [t.post_id],
      foreignColumns: [posts.id],
      name: "fk__post_tags__post_id__posts",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__post_tags__user_id__users",
    }).onDelete("cascade"),
  ],
)

export const subscriptionTags = pgTable(
  "subscription_tags",
  {
    ...base,
    user_id: varchar("user_id").notNull(),
    subscription_id: varchar("subscription_id").notNull(),
    tag: varchar().notNull(),
  },
  t => [
    pgPolicy("subscription_tags_private", {
      for: "all",
      to: authenticatedRole,
      withCheck: userId(t.user_id),
      using: userId(t.user_id),
    }),
    index("ix__subscription_tags__subscription_id").on(t.subscription_id),
    index("ix__subscription_tags__user_id").on(t.user_id),
    unique("ix__subscription_tags__subscription_id_tag").on(
      t.subscription_id,
      t.tag,
    ),
    foreignKey({
      columns: [t.subscription_id],
      foreignColumns: [subscriptions.id],
      name: "fk__subscription_tags__subscription_id__subscriptions",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__subscription_tags__user_id__users",
    }).onDelete("cascade"),
  ],
)

export const replicacheClientGroups = pgTable(
  "replicache_client_groups",
  {
    ...base,
    cvr_version: integer("cvr_version").notNull(),
    user_id: varchar("user_id").notNull(),
  },
  t => [
    pgPolicy("replicache_client_groups_private", {
      for: "all",
      to: authenticatedRole,
      withCheck: userId(t.user_id),
      using: userId(t.user_id),
    }),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__replicache_client_groups__user_id__users",
    }).onDelete("cascade"),
  ],
)

export const replicacheClients = pgTable(
  "replicache_clients",
  {
    ...base,
    last_mutation_id: integer("last_mutation_id").notNull(),
    client_group_id: varchar("client_group_id").notNull(),
  },
  t => [
    publicPolicy("replicache_clients_authenticated"),
    index("ix__replicache_clients__client_group_id").on(t.client_group_id),
    foreignKey({
      columns: [t.client_group_id],
      foreignColumns: [replicacheClientGroups.id],
      name: "fk__replicache_clients__client_group_id__replicache_cli_f7da",
    }).onDelete("cascade"),
  ],
)

export const logs = pgTable(
  "logs",
  {
    ...base,
    user_id: varchar("user_id").notNull(),
    title: varchar().notNull(),
    status: logStatusPgEnum(),
  },
  t => [
    pgPolicy("logs_private", {
      for: "all",
      to: authenticatedRole,
      withCheck: userId(t.user_id),
      using: userId(t.user_id),
    }),
    index("ix__logs__user_id").on(t.user_id),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__logs__user_id__users",
    }).onDelete("cascade"),
  ],
)

export const posts = pgTable(
  "posts",
  {
    ...base,
    root_id: varchar("root_id"), // TODO: NOT NULL
    latest: boolean().default(true).notNull(),
    user_id: varchar("user_id").notNull(),
    title: varchar(),
    description: varchar(),
    body: varchar(),
    json: varchar().notNull(),
    published_at: timestamp("published_at", {
      withTimezone: true,
    }),
    sent_at: timestamp("sent_at", {
      withTimezone: true,
    }),
    archived_at: timestamp("archived_at", {
      withTimezone: true,
    }),
    image_header_url: varchar("image_header_url"),
    pinned_at: timestamp("pinned_at", {
      withTimezone: true,
    }),
    canonical_url: varchar("canonical_url"),
    meta_title: varchar("meta_title"),
    meta_description: varchar("meta_description"),
    bookmark_id: varchar("bookmark_id"),
  },
  t => [
    pgPolicy("posts_private", {
      for: "all",
      to: authenticatedRole,
      withCheck: userId(t.user_id),
      using: userId(t.user_id),
    }),
    index("ix__posts__user_id").on(t.user_id),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__posts__user_id__users",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.bookmark_id],
      foreignColumns: [bookmarks.id],
      name: "fk__posts__bookmark_id__bookmarks",
    }).onDelete("set null"),
  ],
)

export const recentPosts = pgTable(
  "recent_posts",
  {
    ...base,
    post_id: varchar("post_id").notNull(),
    user_id: varchar("user_id").notNull(),
  },
  t => [
    pgPolicy("recent_posts_private", {
      for: "all",
      to: authenticatedRole,
      withCheck: userId(t.user_id),
      using: userId(t.user_id),
    }),
    foreignKey({
      columns: [t.post_id],
      foreignColumns: [posts.id],
      name: "fk__recent_posts__post_id__posts",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__recent_posts__user_id__users",
    }).onDelete("cascade"),
  ],
)

export type Story = typeof stories.$inferSelect
export type Publisher = typeof publishers.$inferSelect
export type Entry = typeof entries.$inferSelect
export type Bookmark = typeof bookmarks.$inferSelect
export type BookmarkTag = typeof bookmarkTags.$inferSelect
export type PostTag = typeof postTags.$inferSelect
export type SubscriptionTag = typeof subscriptionTags.$inferSelect
export type User = typeof users.$inferSelect
export type Log = typeof logs.$inferSelect
export type Post = typeof posts.$inferSelect
export type ReplicacheCvr = typeof replicacheCvr.$inferSelect
export type ReplicacheClientGroup = typeof replicacheClientGroups.$inferSelect
export type ReplicacheClient = typeof replicacheClients.$inferSelect
export type AlembicVersion = typeof alembicVersion.$inferSelect
export type RecentPublisher = typeof recentPublishers.$inferSelect
export type Subscription = typeof subscriptions.$inferSelect
